import React, { useState } from "react";
import { observer } from "mobx-react";
import { ExpandMoreRounded } from "@mui/icons-material";
import {
    Box,
    Collapse,
    Fade,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useCurrentChatStore } from "../Stores/useCurrentChat";

export const ThinkingMessage = observer(() => {
    const store = useCurrentChatStore();
    const [expanded, setExpanded] = useState(false);
    const messages = store.currentThinkingMessages;
    const latestMessage = messages.at(-1);
    const previousMessages = messages.slice(0, Math.max(0, messages.length - 1));
    const hasPreviousMessages = previousMessages.length > 0;

    if (!latestMessage) return null;

    const toggleExpanded = () => {
        if (hasPreviousMessages) {
            setExpanded((current) => !current);
        }
    };

    return (
        <Fade in={true} timeout={300}>
            <Box
                sx={{
                    px: 2,
                    pb: 1,
                    minWidth: 0,
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflowWrap: "anywhere",
                }}
            >
                <Box
                    role={hasPreviousMessages ? "button" : undefined}
                    tabIndex={hasPreviousMessages ? 0 : undefined}
                    aria-expanded={hasPreviousMessages ? expanded : undefined}
                    onClick={toggleExpanded}
                    onKeyDown={(event) => {
                        if (
                            hasPreviousMessages &&
                            (event.key === "Enter" || event.key === " ")
                        ) {
                            event.preventDefault();
                            toggleExpanded();
                        }
                    }}
                    sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minHeight: 28,
                        width: "100%",
                        borderRadius: "4px",
                        cursor: hasPreviousMessages ? "pointer" : "default",
                        color: theme.palette.text.secondary,
                    })}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            fontStyle: "italic",
                            fontSize: "13px",
                            lineHeight: 1.45,
                            whiteSpace: "normal",
                        }}
                    >
                        {latestMessage}
                    </Typography>

                    {hasPreviousMessages && (
                        <Tooltip
                            title={
                                expanded
                                    ? "Hide thinking updates"
                                    : "Show thinking updates"
                            }
                        >
                            <IconButton
                                aria-label={
                                    expanded
                                        ? "Hide thinking updates"
                                        : "Show thinking updates"
                                }
                                size="small"
                                sx={{
                                    width: 24,
                                    height: 24,
                                    padding: 0,
                                    flexShrink: 0,
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleExpanded();
                                }}
                            >
                                <ExpandMoreRounded
                                    sx={{
                                        fontSize: 18,
                                        transform: expanded
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        transition:
                                            "transform 120ms ease-in-out",
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {hasPreviousMessages && (
                    <Collapse in={expanded} timeout={160}>
                        <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                            {previousMessages.map((message, index) => (
                                <Typography
                                    key={`${message}-${index}`}
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "12px",
                                        lineHeight: 1.35,
                                    }}
                                >
                                    {message}
                                </Typography>
                            ))}
                        </Stack>
                    </Collapse>
                )}
            </Box>
        </Fade>
    );
});
